import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ControlOutlined, EditOutlined, PlusOutlined, LockOutlined } from "@ant-design/icons";
import IconButton from "@mui/material/IconButton";
import Button from "src/Components/Button";
import TextInput from "src/Components/TextInput";
import Loading from "src/Components/Loading/Loading";
import Modal from "src/Components/Modal/Modal";
import Pagination from "src/Components/Pagination";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { TableLoadingFrame } from "src/Components/Loading/TableSkeleton";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { Header } from "src/Interfaces/TableInterfaces";
import { ConsumerParameters, ConsumerManualParameter, ParameterEdit, ParameterKind, ParameterValue } from "src/Interfaces/systemParameters";
import { getConsumerParameters, getConsumerManualParameters, getConsumerSymbols, saveConsumerParameters } from "src/Requests/systemParameters";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { matchesDropdownSearch } from "src/Utils/dropdownSearch";
import { addSeperatorToInput } from "src/helper";
import ParameterField from "./ParameterField";
import ParameterSourceToggle from "./ParameterSourceToggle";

const headers:Header[]=[
  {headerName:"symbol",translateHeaderName:"نماد",positionTypeStr:"FirstPage"},
  {headerName:"volatilityPercent",translateHeaderName:"نوسان‌پذیری دستی (%)",positionTypeStr:"FirstPage"},
  {headerName:"dividendYieldPercent",translateHeaderName:"بازده نقدی دستی (%)",positionTypeStr:"FirstPage"},
  {headerName:"edit",translateHeaderName:"ویرایش",positionTypeStr:"FirstPage"},
];
const pageSize=50;
const valid=(value:number|null)=>value!==null && Number.isFinite(value) && value>=0;
const field=(manualPercent:number|null,useCentral:boolean):ParameterValue=>({manualPercent,useCentral,centralPercent:null,effectivePercent:useCentral?null:manualPercent});

type EditorProps = {
  consumer: { id: string; title: string };
  onDirtyChange: (dirty: boolean) => void;
};

export default function ConsumerParametersEditor(props: EditorProps) {
  if (props.consumer.id === "marketwatch") return <section className="rounded-lg border border-slate-200 bg-white p-5" aria-label="پارامترهای مارکت‌واچ">
    <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-700">
      <ControlOutlined aria-hidden="true" />{props.consumer.title}
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {["نوسان‌پذیری", "نرخ بهره", "بازده نقدی"].map(title => <div key={title} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="mb-3 text-xs text-slate-600">{title}</div>
        <div className="flex items-center gap-2 text-sm font-medium text-sky-700"><LockOutlined aria-hidden="true" />مرکزی</div>
      </div>)}
    </div>
  </section>;
  return <EditableConsumerParametersEditor {...props} />;
}

function EditableConsumerParametersEditor({ consumer, onDirtyChange }: {
  consumer: { id: string; title: string };
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [values,setValues]=useState<ConsumerParameters|null>(null);
  const [rows,setRows]=useState<ConsumerManualParameter[]>([]);
  const [loading,setLoading]=useState(true);
  const [revision,setRevision]=useState(0);
  const [saving,setSaving]=useState(false);
  const [dirty,setDirty]=useState(false);
  const [error,setError]=useState("");
  const [search,setSearch]=useState("");
  const [page,setPage]=useState(1);
  const [editing,setEditing]=useState<ConsumerManualParameter|null>(null);
  const [editDirty,setEditDirty]=useState(false);
  const [editError,setEditError]=useState("");
  const [adding,setAdding]=useState(false);
  const [symbols,setSymbols]=useState<DropdownItem[]>([]);
  const [symbolsLoading,setSymbolsLoading]=useState(false);

  useEffect(()=>onDirtyChange(dirty||editDirty),[dirty,editDirty,onDirtyChange]);
  useEffect(()=>{
    const controller=new AbortController();
    setLoading(true);setError("");
    Promise.all([getConsumerParameters(consumer.id,"",controller.signal),getConsumerManualParameters(consumer.id,controller.signal)])
      .then(([settings,manual])=>{if(!controller.signal.aborted){setValues(settings);setRows(manual);setDirty(false);}})
      .catch(e=>{if(!controller.signal.aborted){packetErrorHandler(e);setError("دریافت پارامترها ناموفق بود.");}})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return()=>controller.abort();
  },[consumer.id,revision]);
  useEffect(()=>{
    if(!adding)return;
    const controller=new AbortController();
    setSymbols([]);setSymbolsLoading(true);
    getConsumerSymbols(consumer.id,controller.signal)
      .then(items=>{if(!controller.signal.aborted)setSymbols(items);})
      .catch(e=>{if(!controller.signal.aborted){packetErrorHandler(e);setEditError("دریافت نمادها انجام نشد؛ فرم را دوباره باز کنید.");}})
      .finally(()=>{if(!controller.signal.aborted)setSymbolsLoading(false);});
    return()=>controller.abort();
  },[adding,consumer.id]);

  const changeRate=(value:ParameterValue)=>{
    if(values){setValues({...values,riskFreeRate:value,sources:{...values.sources,riskFreeRate:value.useCentral}});setDirty(true);setError("");}
  };
  const changeSource=(key:"volatility"|"dividendYield",useCentral:boolean)=>{
    if(values){setValues({...values,sources:{...values.sources,[key]:useCentral}});setDirty(true);setError("");}
  };
  const save=async()=>{
    if(!values)return;
    if(!values.sources.riskFreeRate && !valid(values.riskFreeRate.manualPercent)){setError("مقدار دستی نرخ بهره را کامل کنید.");return;}
    const input:ParameterEdit[]=[
      {parameter:ParameterKind.RiskFreeRate,baseInstrumentId:"",useCentral:values.sources.riskFreeRate,manualPercent:values.riskFreeRate.manualPercent},
      {parameter:ParameterKind.Volatility,baseInstrumentId:"",useCentral:values.sources.volatility,manualPercent:null},
      {parameter:ParameterKind.DividendYield,baseInstrumentId:"",useCentral:values.sources.dividendYield,manualPercent:null},
    ];
    setSaving(true);setError("");
    try{await saveConsumerParameters(consumer.id,input);setDirty(false);toast.success("با موفقیت انجام شد");setRevision(v=>v+1);}
    catch(e){packetErrorHandler(e);setError("ذخیره انجام نشد؛ دوباره تلاش کنید.");}
    finally{setSaving(false);}
  };
  const closeEditor=()=>{if(!saving){setEditing(null);setAdding(false);setEditDirty(false);setEditError("");}};
  const saveManual=async()=>{
    if(!editing||!values)return;
    if(!editing.baseInstrumentId){setEditError("نماد را انتخاب کنید.");return;}
    const original=rows.find(row=>row.baseInstrumentId===editing.baseInstrumentId);
    const candidates:ParameterEdit[]=[
      {parameter:ParameterKind.Volatility,baseInstrumentId:editing.baseInstrumentId,useCentral:values.sources.volatility,manualPercent:editing.volatilityPercent},
      {parameter:ParameterKind.DividendYield,baseInstrumentId:editing.baseInstrumentId,useCentral:values.sources.dividendYield,manualPercent:editing.dividendYieldPercent},
    ];
    const input=candidates.filter(item=>!item.useCentral && (item.manualPercent!==null ||
      (item.parameter===ParameterKind.Volatility ? original?.volatilityPercent : original?.dividendYieldPercent)!=null));
    if(!input.length || input.some(item=>!valid(item.manualPercent))){setEditError("حداقل یک مقدار دستی وارد کنید؛ مقدار ثبت‌شده را نمی‌توان خالی کرد.");return;}
    setSaving(true);setEditError("");
    try{
      await saveConsumerParameters(consumer.id,input);
      setRows(items=>(adding?[...items,editing]:items.map(item=>item.baseInstrumentId===editing.baseInstrumentId?editing:item))
        .sort((a,b)=>a.symbol.localeCompare(b.symbol,"fa")));
      if(adding){setSearch("");setPage(1);}
      setEditing(null);setAdding(false);setEditDirty(false);toast.success("با موفقیت انجام شد");
    }catch(e){packetErrorHandler(e);setEditError("ذخیره انجام نشد؛ دوباره تلاش کنید.");}
    finally{setSaving(false);}
  };
  const filtered=rows.filter(row=>matchesDropdownSearch(row.symbol,search));
  const currentPage=Math.min(page,Math.max(1,Math.ceil(filtered.length/pageSize)));
  const offset=(currentPage-1)*pageSize;
  const manualCell=(amount:number|null,useCentral:boolean)=><span dir="ltr" className={useCentral?"opacity-50":""}>{amount===null?"—":`${addSeperatorToInput(String(amount))}%`}</span>;

  return <section aria-label={`پارامترهای ${consumer.title}`} className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ControlOutlined aria-hidden="true" className="text-sky-600" />{consumer.title}</h2>
      <div className="flex gap-2">
        {dirty?<div className="w-20"><Button text="انصراف" variant="outlined" disabled={saving||loading} onClick={()=>setRevision(v=>v+1)} /></div>:null}
        <div className="w-24"><Button text="ذخیره" variant="contained" onClick={save} loading={saving&&!editing} disabled={saving||loading||!dirty||!!editing} /></div>
      </div>
    </header>
    {loading&&!values?<div className="flex justify-center px-5 py-3"><Loading className="!text-sky-600" /></div>:null}
    {error?<p role="alert" className="px-5 pt-4 text-xs text-red-600">{error}</p>:null}
    {values?<div className="flex flex-col gap-5 p-5">
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
        <div className="mb-4 text-xs font-medium text-slate-500">منبع پارامترها برای همهٔ نمادهای {consumer.title}</div>
        <div className="grid gap-5 md:grid-cols-3">
          <ParameterField label="نرخ بهره" value={values.riskFreeRate} onChange={changeRate} disabled={saving||loading||!!editing} />
          <ParameterSourceToggle label="نوسان‌پذیری" useCentral={values.sources.volatility} onChange={v=>changeSource("volatility",v)} disabled={saving||loading||!!editing} />
          <ParameterSourceToggle label="بازده نقدی" useCentral={values.sources.dividendYield} onChange={v=>changeSource("dividendYield",v)} disabled={saving||loading||!!editing} />
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 px-3">
          <h3 className="text-xs font-semibold text-slate-700">مقادیر دستی نمادها</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-56"><TextInput name="manualSymbolSearch" label="جستجوی نماد" value={search} onChange={(_,v)=>{setSearch(v??"");setPage(1);}} /></div>
            <div className="w-32"><Button text={<span className="flex items-center gap-2"><PlusOutlined aria-hidden="true" />افزودن نماد</span>} variant="outlined"
              disabled={saving||loading||dirty||(values.sources.volatility&&values.sources.dividendYield)}
              onClick={()=>{setAdding(true);setEditing({baseInstrumentId:"",symbol:"",volatilityPercent:null,dividendYieldPercent:null});setEditDirty(false);setEditError("");}} /></div>
          </div>
        </div>
        <TableLoadingFrame isLoading={loading} columns={headers.length} table={
          <CommonTableUI reports={{translatedHeaders:headers,report:filtered.slice(offset,offset+pageSize).map((row,i)=>({...row,id:offset+i+1,row:offset+i+1}))}}
            tableBoxClassName="max-h-[50vh]" scrollKey={`consumer-manual-${consumer.id}`}
            renderCell={(row,header)=>{
              if(header.headerName==="volatilityPercent")return manualCell(row.volatilityPercent,values.sources.volatility);
              if(header.headerName==="dividendYieldPercent")return manualCell(row.dividendYieldPercent,values.sources.dividendYield);
              if(header.headerName==="edit")return <IconButton size="small" color="primary" aria-label={`ویرایش ${row.symbol}`} title={`ویرایش ${row.symbol}`}
                disabled={saving||loading||dirty||(values.sources.volatility&&values.sources.dividendYield)}
                onClick={()=>{setAdding(false);setEditing(row);setEditDirty(false);setEditError("");}}><EditOutlined /></IconButton>;
            }} />
        } footer={!loading?<Pagination count={filtered.length} offset={offset} pageSize={pageSize} onChange={setPage} />:null} />
      </div>
    </div>:null}
    {editing&&values?<Modal title={adding?"افزودن مقادیر دستی":`مقادیر دستی ${editing.symbol}`} onClose={closeEditor} modalBoxClassName="!min-w-0 !min-h-0 !w-[480px] !max-w-[95vw] !p-0" UI={
      <div className="flex flex-col gap-5 px-6 pb-5 pt-12" dir="rtl">
        <h2 className="text-sm font-semibold text-slate-800">{editing.symbol?`مقادیر دستی ${editing.symbol}`:"افزودن مقادیر دستی"}</h2>
        {adding?<StaticDropdown dropdown={{name:"manualSymbol",placeholder:"نماد",extraLabel:true,disabled:saving||symbolsLoading}}
          options={symbols.filter(symbol=>!rows.some(row=>row.baseInstrumentId===String(symbol.id)))} value={editing.baseInstrumentId}
          onChange={(_,id)=>{
            const symbol=symbols.find(item=>String(item.id)===String(id));
            setEditing({...editing,baseInstrumentId:symbol?String(symbol.id):"",symbol:symbol?.name??""});setEditDirty(true);setEditError("");
          }} />:null}
        <div className="grid gap-5 sm:grid-cols-2">
          <ParameterField label="نوسان‌پذیری" value={field(editing.volatilityPercent,values.sources.volatility)} withSourceToggle={false} disabled={saving}
            onChange={v=>{setEditing({...editing,volatilityPercent:v.manualPercent});setEditDirty(true);setEditError("");}} />
          <ParameterField label="بازده نقدی" value={field(editing.dividendYieldPercent,values.sources.dividendYield)} withSourceToggle={false} disabled={saving}
            onChange={v=>{setEditing({...editing,dividendYieldPercent:v.manualPercent});setEditDirty(true);setEditError("");}} />
        </div>
        {editError?<p role="alert" className="text-xs text-red-600">{editError}</p>:null}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <div className="w-24"><Button text="انصراف" variant="outlined" disabled={saving} onClick={closeEditor} /></div>
          <div className="w-24"><Button text="ذخیره" variant="contained" loading={saving} disabled={saving||!editDirty||!editing.baseInstrumentId||(adding&&symbolsLoading)} onClick={saveManual} /></div>
        </div>
      </div>} />:null}
  </section>;
}
